import { useState } from 'react';
import { Link } from 'react-router-dom';
import ImageViewer from 'react-simple-image-viewer';
import { icons } from '../../../../../configs/Icons';
import { ImageUploadingIcon } from '../../../../utils/SVGIcon';
import { GetDate, GetTime } from '../../../../utils/Show';
import { mkRelativeUrl } from '../../../../utils/URL';
import { getFileIcon } from '../../../../utils/getFileIcon';

function CloseButton({ closeClick }) {
    return (
        <button
            type="button"
            className="absolute -top-5 -right-3.5 w-7 h-7 p-1  text-white transition-colors duration-200 bg-[#F50303] flex items-center justify-center cursor-pointer rounded-full border border-[#FF1B28]/10"
            onClick={closeClick}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
            >
                <path
                    d="M7.24326 5.99893L10.8841 2.35814C11.0506 2.1926 11.1426 1.97124 11.1426 1.73616C11.1426 1.50108 11.0506 1.28069 10.8841 1.11417C10.553 0.782124 9.97313 0.780165 9.6401 1.11417L5.9993 4.75497L2.3585 1.11417C2.01568 0.77135 1.45737 0.77135 1.11454 1.11417C0.771717 1.457 0.771717 2.01433 1.11454 2.35814L4.75534 5.99893L1.11454 9.63973C0.948027 9.80624 0.856934 10.0276 0.856934 10.2617C0.856934 10.4968 0.948027 10.7182 1.11454 10.8837C1.44757 11.2177 2.02841 11.2157 2.3585 10.8837L5.9993 7.2429L9.6401 10.8837C9.80661 11.0502 10.028 11.1423 10.2621 11.1423C10.4972 11.1423 10.7195 11.0502 10.8841 10.8837C11.0506 10.7182 11.1426 10.4968 11.1426 10.2627C11.1426 10.0286 11.0506 9.80624 10.8841 9.63973L7.24326 5.99893Z"
                    fill="currentColor"
                />
            </svg>
        </button>
    );
}

function FileViewer({ mode, url, closeClick }) {
    function getFileExtension() {
        const parts = url.split('.');
        if (parts.length > 1) {
            return parts[parts.length - 1];
        }
        return '';
    }

    const extension = getFileExtension();

    if (
        !mode ||
        url.includes('png') ||
        url.includes('jpg') ||
        url.includes('jpeg') ||
        url.includes('gif')
    ) {
        return null;
    }
    return (
        <div
            className={`fixed top-0 left-0 w-full h-full z-50 bg-[rgb(4_43_58_/_0.5)] ${
                extension === 'doc' || extension === 'docx' ? 'invisible' : 'visible'
            }`}
        >
            <div className="fixed w-[900px] h-[700px] rounded-10 bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
                <iframe
                    src={url}
                    frameBorder="0"
                    allowFullScreen
                    seamless
                    title="File"
                    className="w-full h-full bg-white overflow-hidden border-none rounded-10"
                />
                <CloseButton closeClick={closeClick} />
            </div>
        </div>
    );
}

function Attachments({ data }) {
    const [fileUrl, setFileUrl] = useState({ mode: false, url: '' });
    const [currentImage, setCurrentImage] = useState(0);
    const [isViewerOpen, setIsViewerOpen] = useState(false);

    const openImageViewer = (index) => {
        setCurrentImage(index);
        setIsViewerOpen(true);
    };

    const closeImageViewer = () => {
        setCurrentImage(0);
        setIsViewerOpen(false);
    };

    const handleFileClick = (item, index) => {
        // if video format
        if (
            item?.fileUrl.includes('.mp4') ||
            item?.fileUrl.includes('.mov') ||
            item?.fileUrl.includes('.avi')
        ) {
            window.open(item?.fileUrl);
            return;
        }

        // includes images format
        if (
            item?.fileUrl.includes('png') ||
            item?.fileUrl.includes('jpg') ||
            item?.fileUrl.includes('jpeg') ||
            item?.fileUrl.includes('gif')
        ) {
            openImageViewer(index);
            setIsViewerOpen(true);
        }

        setFileUrl((d) => ({ ...d, mode: true, url: item?.fileUrl }));
    };

    if (!data?.length) {
        return null;
    }
    return (
        <div className="pt-1">
            {/* <div className="mb-1">
                <span className="font-medium">Attachments:</span>
            </div> */}

            <ul className="flex gap-3">
                {data?.map((item) => (
                    <li key={item?.id}>
                        <button
                            type="button"
                            className="w-10 h-10 relative rounded-5 hover:scale-105"
                            onClick={() => handleFileClick(item)}
                        >
                            {getFileIcon(item?.fileUrl)}
                        </button>
                    </li>
                ))}

                <div className="image-viewer z-20">
                    {isViewerOpen && (
                        <ImageViewer
                            src={data?.map((item) => item?.fileUrl)}
                            currentIndex={currentImage}
                            disableScroll={false}
                            closeOnClickOutside
                            onClose={closeImageViewer}
                        />
                    )}
                </div>

                <FileViewer
                    mode={fileUrl?.mode}
                    url={fileUrl?.url}
                    closeClick={() => setFileUrl({ mode: false, url: '' })}
                />
            </ul>
        </div>
    );
}

function ConversationThread({ item }) {
    const role = item?.creatorProfile?.role;
    const fullName = `${item?.creatorProfile?.firstName} ${item?.creatorProfile?.lastName}`;

    return (
        <div className={`flex items-start gap-x-3 py-4 border-b border-primary-50 `}>
            <div className="overflow-hidden h-8 w-h-8 rounded-full mt-1">
                <span className="block w-full h-full">
                    <img
                        src={icons.defaultAvatar}
                        alt="avatar"
                        className="w-full h-full block object-cover object-top"
                    />
                </span>
            </div>

            <div className="w-[calc(100%_-_32px)]">
                <div className="flex items-center gap-x-2">
                    <div className="flex">
                        {role === 'admin' ? (
                            <span
                                className={`font-medium capitalize ${
                                    role === 'admin' ? 'text-secondary' : 'text-[#FC9700]'
                                }`}
                            >
                                {fullName || '--'}
                            </span>
                        ) : (
                            <Link
                                to={mkRelativeUrl(`/customers/${item?.creatorProfile?.id}/info`)}
                                className={`font-medium capitalize underline ${
                                    role === 'admin' ? 'text-secondary' : 'text-[#FC9700]'
                                }`}
                            >
                                {fullName || '--'}
                            </Link>
                        )}
                        <span
                            className={`flex items-center text-[11px] capitalize max-h-5 text-white px-2 rounded-5 ml-2 ${
                                role === 'admin' ? 'bg-secondary' : 'bg-[#FC9700]'
                            }`}
                        >
                            {role || '--'}
                        </span>
                    </div>
                    <div className="text-xs flex gap-1 text-gray-600">
                        <p>{GetTime(item?.timeCreated)}</p>
                        {` | `}
                        <p>{GetDate(item?.timeCreated)}</p>
                    </div>
                </div>
                <div className="">
                    <div className="mb-3">
                        <span className="whitespace-pre-line">{item?.message}</span>
                    </div>

                    <Attachments data={item?.files} />
                </div>
            </div>
        </div>
    );
}

function SupportConversation({ data }) {
    const conversations = data?.messages;
    return (
        <div className=" bg-primary-50 rounded-10 px-3">
            <div className="[&>*:last-of-type]:!border-b-transparent">
                {conversations?.[0]?.demo && (
                    <div className="size-30 text-secondary mx-auto">
                        <ImageUploadingIcon />
                    </div>
                )}

                {!conversations?.[0]?.demo &&
                    conversations?.map((item) => <ConversationThread key={item?.id} item={item} />)}
            </div>
        </div>
    );
}

export default SupportConversation;
